/* Theme custom file */
/**
 * Paginated list (alternate) — extends the native Horizon `PaginatedList` to add
 * a "pause every N pages" interruption to infinite scroll, surfaced with a manual
 * "Load more" button to resume.
 *
 * Why subclass instead of reimplement: the native fetch/observe/render machinery
 * lives in private methods on `PaginatedList`, so we let the base drive scrolling
 * and only gate it from the outside — disconnecting the scroll observer at each
 * N-page boundary and re-arming it on button click. See
 * docs/specs/plp.md and the BRD-381 infinite-scroll rework.
 *
 * Attributes:
 *   pages-before-pause  Integer N. Scroll auto-loads pages until the highest
 *                       loaded page number is a multiple of N, then pauses and
 *                       reveals the button. 0 (default) = uncapped infinite scroll.
 *                       Example N=3: pauses at pages 3, 6, 9 … each click resumes
 *                       auto-scroll for the next batch.
 *
 * Optional refs (in addition to the base): loadMoreButton.
 *
 * Note: the consuming section must include a `[ref="cardGallery"]` marker element
 * so the base's aspect-ratio helper constructs; without it the base throws on the
 * first appended page. The alternate product card has no card gallery of its own,
 * so a hidden marker stands in (the helper is design-mode only and inert for it).
 */
import PaginatedList from '@theme/paginated-list';
import { ThemeEvents } from '@theme/events';

export default class PaginatedListAlternate extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #gridObserver;

  connectedCallback() {
    super.connectedCallback();

    this.refs.loadMoreButton?.addEventListener('click', this.#onLoadMore);

    // No interruption requested — behave exactly like the native base.
    if (this.#pagesBeforePause <= 0) return;

    document.addEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    this.#observeGrid();

    // Evaluate once on load in case the first page already sits on a boundary
    // (N = 1) or the page is short enough to satisfy the observer immediately.
    this.#evaluatePause();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#gridObserver?.disconnect();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    this.refs.loadMoreButton?.removeEventListener('click', this.#onLoadMore);
  }

  /** @returns {number} The configured N, or 0 when uncapped. */
  get #pagesBeforePause() {
    return Number(this.getAttribute('pages-before-pause')) || 0;
  }

  /** (Re)attach the grid observer so each rendered page triggers an evaluation. */
  #observeGrid() {
    this.#gridObserver?.disconnect();

    const { grid } = this.refs;
    if (!grid) return;

    this.#gridObserver = new MutationObserver(this.#evaluatePause);
    this.#gridObserver.observe(grid, { childList: true });
  }

  /** @returns {number} Highest page number currently rendered in the grid. */
  #maxLoadedPage() {
    const { grid } = this.refs;
    if (!grid) return 0;

    let max = 0;
    for (const card of grid.querySelectorAll('[ref="cards[]"]')) {
      const page = Number(card.getAttribute('data-page'));
      if (page > max) max = page;
    }
    return max;
  }

  /** @returns {number} Total page count, from the grid's data-last-page. */
  #lastPage() {
    return Number(this.refs.grid?.dataset.lastPage) || 0;
  }

  /** Pause on an N-page boundary (more pages remaining), otherwise stay/resume active. */
  #evaluatePause = () => {
    const n = this.#pagesBeforePause;
    if (n <= 0) return;

    const maxPage = this.#maxLoadedPage();
    const lastPage = this.#lastPage();
    const atBoundary = maxPage > 0 && maxPage % n === 0;
    const hasMore = lastPage === 0 || maxPage < lastPage;

    if (atBoundary && hasMore) {
      this.#pause();
    } else {
      this.#setButtonVisible(false);
    }
  };

  /** Stop auto-loading and reveal the button. */
  #pause() {
    this.infinityScrollObserver?.disconnect();
    this.#setButtonVisible(true);
  }

  /** Hide the button and re-arm the scroll observer for the next batch. */
  #onLoadMore = () => {
    this.#setButtonVisible(false);

    const { viewMoreNext, viewMorePrevious } = this.refs;
    if (viewMoreNext) this.infinityScrollObserver?.observe(viewMoreNext);
    if (viewMorePrevious) this.infinityScrollObserver?.observe(viewMorePrevious);
  };

  /** Re-render swaps the grid subtree, so re-attach and re-evaluate afterwards. */
  #onFilterUpdate = () => {
    requestAnimationFrame(() => {
      this.#observeGrid();
      this.#evaluatePause();
    });
  };

  /** @param {boolean} visible */
  #setButtonVisible(visible) {
    const { loadMoreButton } = this.refs;
    if (loadMoreButton) loadMoreButton.hidden = !visible;
  }
}

if (!customElements.get('results-list-alternate')) {
  customElements.define('results-list-alternate', PaginatedListAlternate);
}
